import * as opentype from "opentype.js";
import { Product, ProductCategory } from "./types/campaigns";
import { CreateCampaignFormData } from "./components/CreateCampaignModal/types";
import { cloneDeep } from 'lodash'
import { jwtDecode } from "jwt-decode";
import { UserPayload } from "./types/user";

export const errorsToString = (raw: string | string[] | any) => {
  return typeof raw === 'string'
    ? raw
    : Array.isArray(raw) ? raw[0] : ''
}

export const getProductsToCategoriesMap = (products: Product[], categories: ProductCategory[]): Map<number, Product[]> => {
  return new Map<number, Product[]>(
    categories.map((c) => {
      return [
        c.id,
        products.filter(p => p.category_id === c.id)
      ]
    })
  );
}

export const unmaskPhone = (phone: string): string => {
  const digits = phone.replace(/\D/g, "");

  if (digits.length === 10) {
    return `+1${digits}`;
  }

  if (digits.length === 11 && digits.startsWith("1")) {
    return `+${digits}`;
  }

  return `+${digits}`;
}


// --- helpers ---
export const formatKiB = (bytes: number) => {
  const kib = bytes / 1024;
  return `${Math.trunc(kib)} KiB`;
}

export const weightToName = (w: number | undefined) => {
  if (!w) return "Normal";
  if (w <= 150) return "Thin"; // 100
  if (w <= 250) return "Extra Light"; // 200
  if (w <= 350) return "Light"; // 300
  if (w <= 450) return "Normal"; // 400
  if (w <= 550) return "Medium"; // 500
  if (w <= 650) return "Semi Bold"; // 600
  if (w <= 750) return "Bold"; // 700
  if (w <= 850) return "Extra Bold"; // 800
  return "Black"; // 900
}

export const extensionOf = (name: string) => {
  const m = /\.([a-z0-9]+)$/i.exec(name);
  return m ? m[1].toLowerCase() : "";
}


export interface ParsedFontInfo {
  family?: string;
  subfamily?: string; // e.g., Regular, Italic, Bold
  weight?: number; // numeric usWeightClass
  style?: "normal" | "italic" | "oblique";
  fullName?: string;
}

// --- parsing from ArrayBuffer using opentype.js ---
export const parseFont = async (ab: ArrayBuffer) => {
  try {
    const font = opentype.parse(ab as unknown as ArrayBuffer);
    const os2 = font.tables?.os2;
    const head = font.tables?.head;
    const names = (font as any).names || {};

    const family = names?.preferredFamily?.en || names?.fontFamily?.en;
    const subfamily = names?.preferredSubfamily?.en || names?.fontSubfamily?.en;

    // weight from OS/2 usWeightClass (100..900)
    const weight: number | undefined = os2?.usWeightClass;
    const fullName = names?.fullName?.en;

    // style detection: Italic bit in fsSelection or macStyle
    let style: "normal" | "italic" | "oblique" = "normal";
    const fsSel = os2?.fsSelection ?? 0;
    const macStyle = head?.macStyle ?? 0;
    const ITALIC_FSEL = 1 << 0; // bit 0
    const ITALIC_MAC = 1 << 1; // bit 1
    if ((fsSel & ITALIC_FSEL) || (macStyle & ITALIC_MAC)) style = "italic";

    return { family, subfamily, weight, style, fullName } as ParsedFontInfo;
  } catch (e) {
    console.warn("Failed to parse font via opentype.js", e);
    return null;
  }
}

export const isObject = (value: any) => {
  return value !== null && typeof value === 'object' && !Array.isArray(value);
}


export const makeInlineObject = (obj: any, prefix?: string) => {
  if (!isObject(obj)) {
    return {} as any;
  }
  let res: any = {};
  Object.entries(obj).forEach(([key, value]) => {
    const prefixedKey = prefix ? (prefix + '.' + key) : key;
    if (isObject(value) && !(value instanceof File)) {
      res = {
        ...res,
        ...makeInlineObject(value, prefixedKey)
      };
    } else {
      res[prefixedKey] = value;
    }
  })

  return res;
}


export const stringToColor = (string: string) => {
  let hash = 0;
  let i;

  for (i = 0; i < string.length; i += 1) {
    hash = string.charCodeAt(i) + ((hash << 5) - hash);
  }

  let color = '#';

  for (i = 0; i < 3; i += 1) {
    const value = (hash >> (i * 8)) & 0xff;
    color += `00${value.toString(16)}`.slice(-2);
  }

  return color;
}

export const createCampaignFormToFormData = (data: CreateCampaignFormData) => {

  const formData = new FormData();
  Object.entries(data).forEach(([key, value]) => {
    if (key === "_selectedCardIds") {
      return;
    }
    if (key === 'campaignCards') {
      const copy = cloneDeep(data.campaignCards);
      copy.forEach((c) => {
        if (c.backgroundImg) {
          formData.append(`card_bg_${c.id}`, c.backgroundImg);
          // @ts-ignore
          delete c.backgroundImg;
        }
      });
    }
    if (key === 'productCategories') {
      formData.append(
        "productCategoryIds",
        JSON.stringify(data.productCategories.map(c => c.id))
      );
      return;
    }
    if (key === 'products') {
      formData.append(
        "productsIncludeIds",
        JSON.stringify(data.products.filter(p => p.included).map(p => p.id))
      );
      formData.append(
        "productsExcludeIds",
        JSON.stringify(data.products.filter(p => !p.included).map(p => p.id))
      );
      return
    }

    if (
      ['inclProductsGreaterThan', 'exclProductsLessThan', 'redeemLimitsValue', 'minOrderValue', 'maxOrderValue'].includes(key) &&
      typeof value === 'string'
    ) {
      // @ts-ignore
      formData.append(key, Number(value));
      return
    }

    if (value !== undefined) {
      formData.append(key, JSON.stringify(value));
    }
  })

  return formData;

}

export const getUserPayloadFromToken = (token: string | null): UserPayload | null => {
  if (!token) return null;
  try {
    const decoded = jwtDecode(token) as any;
    return {
      email: decoded?.user?.email,
      userId: decoded?.sub as string,
      userRoleId: +decoded?.user?.userRoleId,
      userRoleType: decoded?.user?.userRoleType,
    };
  } catch (error) {
    console.error("Failed to decode token", error)
    return null;
  }
}

/**
 * Simple hash function for display purposes.
 * Returns a truncated hash like "a94f...12dd"
 *
 * TODO: Implement proper hashing in the backend post-demo.
 * This is a temporary frontend-only solution for demo purposes.
 */
export const hashForDisplay = (value: string | null | undefined): string | undefined => {
  if (!value) return undefined;

  // Simple hash using string char codes
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    const char = value.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }

  // Convert to hex and create truncated display format
  const fullHash = Math.abs(hash).toString(16).padStart(8, '0');
  const prefix = fullHash.slice(0, 4);
  const suffix = fullHash.slice(-4);

  return `${prefix}...${suffix}`;
}